
  import { defineComponent, onMounted, ref, computed } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRoute, useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'offices-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const thumbnail = ref('/media/avatars/blank-image.jpg');
      let file;
      const badges = ref<Array<any>>([]);
      const selectedBadgeId = ref<string | number>('');
      const office = ref<any>(null);

      const selectedBadge = computed(() => {
        return badges.value.find((badge) => badge.id === selectedBadgeId.value);
      });

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('officesUpdating'), [
          translate('offices'),
        ]);
        try {
          badges.value = await store.dispatch(Actions.GET_ALL_BADGES);

          const { data } = await store.dispatch(
            Actions.GET_OFFICE,
            route.params.id
          );
          office.value = data.data;

          if (office.value.business_account_info.badgeId) {
            selectedBadgeId.value = office.value.business_account_info.badgeId;
          }
          if (office.value.logo) thumbnail.value = office.value.logo;
        } catch (error) {
          console.error('Error loading office or badges:', error);
        }
      });

      const previewImage = (e) => {
        file = e.target.files[0];
        thumbnail.value = URL.createObjectURL(file);
      };

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const officeSchema = Yup.object().shape({
        name: Yup.string().label('English Name'),
      });

      const onSubmitUpdate = async (values) => {
        delete values.logo;
        if (submitButton.value) {
          // eslint-disable-next-line
        submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        if (selectedBadgeId.value) {
          values.badgeId = selectedBadgeId.value;
        }
        delete values.badges;
        await store.dispatch(Actions.UPDATE_OFFICE, {
          id: route.params.id,
          data: values,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        const logoFormData = new FormData();
        // eslint-disable-next-line
      logoFormData.append('logo', file);

        if (logoFormData) {
          await store.dispatch(Actions.UPLOAD_OFFICE_LOGO, {
            data: logoFormData,
            id: route.params.id,
          });
        }

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
        submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_OFFICE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'offices-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      const onConvertOfficeToIndividual = async () => {
        const confirmed = await Swal.fire({
          title: translate('areYouSure'),
          text: translate('invalidateOfficeWarning'),
          icon: 'warning',
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('yesConvert'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn btn-danger fw-bold',
            cancelButton: 'btn btn-light fw-bold me-3',
          },
          reverseButtons: true,
        });

        if (!confirmed.isConfirmed) return;

        const whatsappResult = await Swal.fire({
          title: translate('sendWhatsAppNotification'),
          icon: 'question',
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('yesSend'),
          cancelButtonText: translate('noSkip'),
          customClass: {
            confirmButton: 'btn btn-success fw-bold',
            cancelButton: 'btn btn-light fw-bold me-3',
          },
          reverseButtons: true,
        });

        const sendWhatsApp = whatsappResult.isConfirmed;

        try {
          await store.dispatch(Actions.INVALIDATE_OFFICE, {
            id: route.params.id,
            sendWhatsApp,
          });

          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];

          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            await Swal.fire({
              text: translate('SUCCESSFULLY_INVALIDATED_OFFICE'),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
            router.push({ name: 'offices-listing' });
          }
        } catch (error) {
          console.error('Error invalidating office:', error);
        }
      };

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        officeSchema,
        goBack,
        can,
        previewImage,
        thumbnail,
        file,
        office,
        selectedBadge,
        selectedBadgeId,
        onConvertOfficeToIndividual,
        badges,
      };
    },
  });
